import { useState } from "react";
import { Link } from "react-router-dom";
import {
  differenceInDays,
  differenceInMinutes,
  differenceInHours,
  differenceInWeeks,
  differenceInMonths,
} from "date-fns";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { MapPin, Clock } from "lucide-react";


const JobCard = ({ job, status, formattedDatePosted }) => {

  const disabledStatuses = [
    "Applied",
    "Under Review",
    "Rejected",
    "Initial Test Submitted",
    "Training Completed",
    "Final Test Submitted",
    "Approved"
  ];
  const isDisabled = disabledStatuses.includes(status);

  const getDisabledLabel = () => {
    if (status === "Rejected") return "Rejected";
    if (status === "Under Review") return "Under Review";
    if (status === "Approved") return "Approved";
    return "Applied";
  };

  const renderButton = () => {
    if (!job.hasTest) {
      // No test created by admin yet — view-only
      return (
        <Link to={`/job-details/${job._id}`}>
          <Button variant="outline" className="min-w-[120px] border-[#ff9906] text-[#ff9906] hover:bg-[#ff9906]/10">
            View
          </Button>
        </Link>
      );
    }

    if (isDisabled) {
      return (
        <Button
          disabled
          variant="outline"
          className="min-w-[120px] cursor-not-allowed opacity-60"
        >
          {getDisabledLabel()}
        </Button>
      );
    }

    return (
      <Link to={`/job-details/${job._id}`}>
        <Button className="min-w-[120px] bg-[#ff9906] hover:bg-[#ff9906]/90">
          Apply Now
        </Button>
      </Link>
    );
  };

  return (
    <div className="group relative overflow-hidden rounded-lg border bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 shadow-sm transition-all hover:shadow-md dark:shadow-none">
      {/* Featured Tag */}
      {job.featured && (
        <div className="absolute right-0 top-0 h-16 w-16">
          <div className="absolute left-0 top-0 h-2 w-2 bg-[#ff9906]"></div>
          <div className="absolute bottom-0 right-0 h-2 w-2 bg-[#ff9906]"></div>
          <div className="absolute right-[-35px] top-[32px] w-[170px] transform rotate-45 bg-[#ff9906] py-1 text-center text-xs font-semibold text-white">
            Featured
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-xl text-gray-900 dark:text-gray-100 group-hover:text-[#ff9906] transition-colors">
                {job.title}
              </h3>
              {job.isUrgent && (
                <Badge variant="destructive" className="uppercase">
                  Urgent
                </Badge>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-300">
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span>{job.location}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{formattedDatePosted}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Badge variant="secondary">{job.type || "Full Time"}</Badge>
          <Badge variant="secondary">{job.experience || "2-3 Years"}</Badge>
          {job.salary && <Badge variant="secondary">{job.salary}</Badge>}
        </div>

        {/* Description */}
        <p className="mt-4 line-clamp-3 text-sm text-gray-700 dark:text-gray-300">
          {job.description}
        </p>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {job.skills?.slice(0, 3).map((skill) => (
              <Badge key={skill} variant="outline">
                {skill}
              </Badge>
            ))}
            {job.skills?.length > 3 && (
              <Badge variant="outline">+{job.skills.length - 3}</Badge>
            )}
          </div>
          {renderButton()}
        </div>
      </div>
    </div>
  );
};


const Jobs = ({ jobs, loading, error, getJobStatus }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  const getFormattedDatePosted = (datePosted) => {
    const now = new Date();
    const postedDate = new Date(datePosted);
    const minutes = differenceInMinutes(now, postedDate);
    const hours = differenceInHours(now, postedDate);
    const days = differenceInDays(now, postedDate);
    const weeks = differenceInWeeks(now, postedDate);
    const months = differenceInMonths(now, postedDate);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 4) return `${weeks}w ago`;
    return `${months}mo ago`;
  };

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 dark:bg-transparent dark:border-destructive/30 p-6 text-center text-destructive dark:text-destructive">
        {error}
      </div>
    );
  }
  if (!jobs.length) {
    return (
      <div className="rounded-lg border border-muted-foreground/20 dark:border-slate-700 p-6 text-center text-muted-foreground dark:text-gray-300">
        No jobs found.
      </div>
    );
  }

  return (
    <section className="container mx-auto px-4 py-8 pl-0 pr-0">
      {/* Job Listings */}
      <div className="grid gap-4">
        {jobs
          .filter(
            (job) =>
              job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              job.location.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .filter(
            (job) =>
              filterType === "all" ||
              job.type?.toLowerCase() === filterType.toLowerCase()
          )
          .map((job) => (
            <JobCard
              key={job._id}
              job={job}
              status={getJobStatus(job._id)}
              formattedDatePosted={getFormattedDatePosted(job.datePosted)}
            />
          ))}
      </div>
    </section>
  );
};

export default Jobs;
